import { Document, Paragraph, TextRun, HeadingLevel, AlignmentType, BorderStyle, Packer, TableOfContents, ShadingType, TabStopPosition, TabStopType, convertInchesToTwip, PageBreak } from 'docx';
import { saveAs } from 'file-saver';

const PRIMARY_COLOR = '1E40AF';
const DARK_COLOR = '0F172A';
const GRAY_COLOR = '64748B';
const GREEN_COLOR = '10B981';
const AMBER_COLOR = 'F59E0B';
const RED_COLOR = 'EF4444';
const BLUE_COLOR = '3B82F6';

function createTitle(text, level = HeadingLevel.HEADING_1) {
  return new Paragraph({
    text,
    heading: level,
    spacing: { before: 240, after: 120 },
  });
}

function createText(text, options = {}) {
  return new Paragraph({
    spacing: { after: 100 },
    children: [
      new TextRun({
        text,
        size: options.size || 22,
        color: options.color || DARK_COLOR,
        bold: options.bold || false,
        italics: options.italics || false,
        font: 'Calibri',
      }),
    ],
  });
}

function createAlertBox(label, text, color) {
  return new Paragraph({
    spacing: { before: 120, after: 120 },
    border: {
      left: { style: BorderStyle.SINGLE, size: 12, color },
    },
    shading: { type: ShadingType.SOLID, color: 'F8FAFC' },
    indent: { left: convertInchesToTwip(0.15) },
    children: [
      new TextRun({ text: `${label} : `, bold: true, size: 20, color, font: 'Calibri' }),
      new TextRun({ text, size: 20, color: DARK_COLOR, font: 'Calibri' }),
    ],
  });
}

function createStep(index, text) {
  return new Paragraph({
    spacing: { before: 60, after: 60 },
    indent: { left: convertInchesToTwip(0.3) },
    children: [
      new TextRun({ text: `${index}. `, bold: true, size: 22, color: PRIMARY_COLOR, font: 'Calibri' }),
      new TextRun({ text, size: 21, color: DARK_COLOR, font: 'Calibri' }),
    ],
  });
}

function createErrorRow(err) {
  return [
    new Paragraph({
      spacing: { before: 60, after: 30 },
      indent: { left: convertInchesToTwip(0.3) },
      children: [
        new TextRun({ text: 'Probleme : ', bold: true, size: 20, color: RED_COLOR, font: 'Calibri' }),
        new TextRun({ text: err.problem, size: 20, color: DARK_COLOR, font: 'Calibri' }),
      ],
    }),
    new Paragraph({
      spacing: { before: 30, after: 80 },
      indent: { left: convertInchesToTwip(0.3) },
      children: [
        new TextRun({ text: 'Solution : ', bold: true, size: 20, color: GREEN_COLOR, font: 'Calibri' }),
        new TextRun({ text: err.solution, size: 20, color: DARK_COLOR, font: 'Calibri' }),
      ],
    }),
  ];
}

export async function generateGuideDocx(guide) {
  const children = [];

  // ===== TITLE PAGE =====
  children.push(
    new Paragraph({ spacing: { before: 2000 } }),
    new Paragraph({
      alignment: AlignmentType.CENTER,
      spacing: { after: 200 },
      children: [
        new TextRun({ text: guide.title, size: 56, bold: true, color: PRIMARY_COLOR, font: 'Calibri' }),
      ],
    }),
    new Paragraph({
      alignment: AlignmentType.CENTER,
      spacing: { after: 400 },
      children: [
        new TextRun({ text: guide.subtitle || '', size: 24, color: GRAY_COLOR, font: 'Calibri' }),
      ],
    }),
    new Paragraph({
      alignment: AlignmentType.CENTER,
      spacing: { after: 100 },
      children: [
        new TextRun({ text: 'PunchTM - Systeme de pointage industriel', size: 22, color: DARK_COLOR, font: 'Calibri' }),
      ],
    }),
    new Paragraph({
      alignment: AlignmentType.CENTER,
      children: [
        new TextRun({
          text: `Document genere le ${new Date().toLocaleDateString('fr-CA')}`,
          size: 20,
          color: GRAY_COLOR,
          font: 'Calibri',
        }),
      ],
    }),
    new Paragraph({ children: [new PageBreak()] })
  );

  // ===== TABLE OF CONTENTS =====
  children.push(
    new Paragraph({
      text: 'Table des matieres',
      heading: HeadingLevel.HEADING_1,
      spacing: { after: 300 },
    })
  );

  guide.sections.forEach((section, sIdx) => {
    children.push(
      new Paragraph({
        spacing: { before: 80, after: 40 },
        children: [
          new TextRun({ text: `${sIdx + 1}. ${section.title}`, size: 24, bold: true, color: PRIMARY_COLOR, font: 'Calibri' }),
        ],
      })
    );
    section.content?.forEach((sub, subIdx) => {
      children.push(
        new Paragraph({
          spacing: { after: 30 },
          indent: { left: convertInchesToTwip(0.4) },
          children: [
            new TextRun({ text: `${sIdx + 1}.${subIdx + 1}  ${sub.title}`, size: 21, color: GRAY_COLOR, font: 'Calibri' }),
          ],
        })
      );
    });
  });

  children.push(new Paragraph({ children: [new PageBreak()] }));

  // ===== CONTENT =====
  guide.sections.forEach((section, sIdx) => {
    // Section heading
    children.push(
      new Paragraph({
        heading: HeadingLevel.HEADING_1,
        spacing: { before: 400, after: 200 },
        border: {
          bottom: { style: BorderStyle.SINGLE, size: 6, color: PRIMARY_COLOR },
        },
        children: [
          new TextRun({ text: `${sIdx + 1}. ${section.title}`, size: 36, bold: true, color: PRIMARY_COLOR, font: 'Calibri' }),
        ],
      })
    );

    if (section.overview) {
      children.push(createText(section.overview, { color: GRAY_COLOR, italics: true }));
    }

    section.content?.forEach((sub, subIdx) => {
      // Subsection heading
      children.push(
        new Paragraph({
          heading: HeadingLevel.HEADING_2,
          spacing: { before: 300, after: 120 },
          shading: { type: ShadingType.SOLID, color: 'EFF6FF' },
          children: [
            new TextRun({ text: `${sIdx + 1}.${subIdx + 1}  ${sub.title}`, size: 28, bold: true, color: PRIMARY_COLOR, font: 'Calibri' }),
          ],
        })
      );

      // Description
      if (sub.description) {
        children.push(createText(sub.description));
      }

      // When to use
      if (sub.whenToUse) {
        children.push(createAlertBox('Quand utiliser', sub.whenToUse, BLUE_COLOR));
      }

      // Steps
      if (sub.steps?.length) {
        children.push(
          new Paragraph({
            spacing: { before: 160, after: 80 },
            children: [
              new TextRun({ text: 'Etapes', size: 24, bold: true, color: DARK_COLOR, font: 'Calibri' }),
            ],
          })
        );
        sub.steps.forEach((step, i) => {
          children.push(createStep(i + 1, step));
        });
      }

      // Result
      if (sub.result) {
        children.push(createAlertBox('Resultat attendu', sub.result, GREEN_COLOR));
      }

      // Tips
      sub.tips?.forEach(tip => {
        children.push(createAlertBox('Conseil', tip, BLUE_COLOR));
      });

      // Warnings
      sub.warnings?.forEach(w => {
        children.push(createAlertBox('Attention', w, AMBER_COLOR));
      });

      // Errors
      if (sub.errors?.length) {
        children.push(
          new Paragraph({
            spacing: { before: 160, after: 80 },
            children: [
              new TextRun({ text: 'Erreurs frequentes', size: 24, bold: true, color: RED_COLOR, font: 'Calibri' }),
            ],
          })
        );
        sub.errors.forEach(err => {
          children.push(...createErrorRow(err));
        });
      }
    });

    // Page break between sections
    children.push(new Paragraph({ children: [new PageBreak()] }));
  });

  // ===== CREATE DOC =====
  const doc = new Document({
    creator: 'PunchTM',
    title: guide.title,
    description: guide.subtitle,
    styles: {
      default: {
        document: {
          run: { font: 'Calibri', size: 22 },
        },
        heading1: {
          run: { font: 'Calibri', size: 36, bold: true, color: PRIMARY_COLOR },
        },
        heading2: {
          run: { font: 'Calibri', size: 28, bold: true, color: PRIMARY_COLOR },
        },
      },
    },
    sections: [{
      properties: {
        page: {
          margin: {
            top: convertInchesToTwip(1),
            bottom: convertInchesToTwip(0.8),
            left: convertInchesToTwip(1),
            right: convertInchesToTwip(1),
          },
        },
      },
      children,
    }],
  });

  const blob = await Packer.toBlob(doc);
  const today = new Date().toLocaleDateString('fr-CA');
  saveAs(blob, `PunchTM_${guide.id.replace(/-/g, '_')}_${today}.docx`);
}
